/**
 * The labels section inside a menu: one togglable row per label in the registry.
 *
 * Four menus draw this - the mail thread's, the call's, and the right-click menu
 * on each of those lists - and all four need the same non-obvious piece of
 * state, which is why it lives here rather than being written out a fourth time.
 *
 * That piece: a tags patch carries the WHOLE set, and this section deliberately
 * stays open so two labels can be applied in a row ("complaint, needs a reply").
 * Reading the row's own `tags` on the second click would read the value from
 * before the first patch landed, and the second write would erase the first
 * label. `pending` holds what was last sent until the parent's answer arrives,
 * so the second click composes on top of the first, and it is cleared only if
 * nothing newer has been sent since - a slow first response must not flash the
 * list back to a state the operator has already left.
 */
import { useState } from "react";
import { Check } from "lucide-react";
import { cn } from "@wa/ui";
import { labelColor, toggleTag, type CrmLabel } from "../../lib/crmLabels";

export function LabelItems({
  title,
  labels,
  tags,
  onApply,
}: {
  title: string;
  labels: CrmLabel[];
  /** The row's stored set. `?? []` at the call site is deploy insurance: between
   *  a new bundle reaching a browser and the API restarting with the column, a
   *  row arrives with no tags at all. */
  tags: string[];
  onApply: (next: string[]) => Promise<void>;
}) {
  const [pending, setPending] = useState<string[] | null>(null);
  const current = pending ?? tags;

  if (labels.length === 0) return null;

  const apply = (next: string[]) => {
    setPending(next);
    void onApply(next).finally(() => setPending((p) => (p === next ? null : p)));
  };

  return (
    <>
      <div className="crm-msect">{title}</div>
      {labels.map((label) => {
        const on = current.includes(label.id);
        return (
          <button
            key={label.id}
            type="button"
            className={cn("crm-mi", on && "is-on")}
            aria-pressed={on}
            onClick={() => apply(toggleTag(current, labels, label.id))}
          >
            {on ? (
              <Check className="size-4" />
            ) : (
              <i className="crm-lbl-dot" style={{ background: labelColor(label.tone) }} />
            )}
            {label.name}
          </button>
        );
      })}
    </>
  );
}
